import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, JSX, PointerEvent as ReactPointerEvent } from 'react'

import { MIRA_MAX_IMAGES, type MiraImage } from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import type { MiraImageTools } from './browser-images.js'
import {
  CROP_EDGES,
  MIRA_PICKER_ACCEPT,
  WHOLE_IMAGE,
  confirmCrop,
  cropChosen,
  cropFromDrag,
  cropKeepsTooMuch,
  moveCropEdge,
  type CropBox,
  type CropEdge,
} from './images.js'

/**
 * Choosing a photograph, cropping it, and confirming that crop. `AI-004`, `AI-010` to `AI-012`.
 *
 * > `AI-004` `MUST` ... Prescription images are stripped of identifying regions before transmission
 * > where possible, and the payload minimised to what the task needs.
 *
 * **The crop is the product's answer to "where possible", and it is a step not a setting.** A slip
 * carries the patient's name at the top and the server's two payload gates read keys, so neither
 * can see it. This screen shows the photograph, the shopkeeper drags a box over the values, and
 * `confirmCrop` encodes only what is inside the box. Nothing else leaves the device.
 *
 * **Two routes to the same box, because a till is keyboard first (`KB-02`).** A drag is what a
 * finger does on a tablet and what the shop will use. The four number inputs are the same box in
 * percentages: they are how it is reached from a keyboard, how it is adjusted precisely, and, since
 * jsdom has no layout, the route a test can drive without stubbing geometry.
 *
 * **The confirm stays inert until a box has actually been drawn.** The box starts as the whole
 * photograph, so a confirm that accepted that would make `AI-004`'s step a reflex press. What this
 * can promise is that somebody looked at the photograph and chose a part of it; it cannot promise
 * the name is outside the box, which `MIRA_CROP_CONFIRMED` says plainly.
 *
 * `tools` is injected for the reason `browser-images.ts` gives: image decoding, a canvas and
 * `URL.createObjectURL` are all absent in jsdom, so the real implementation cannot be exercised by
 * a suite and everything that can be wrong lives in `images.ts` instead.
 */

export interface MiraImagesProps {
  readonly images: readonly MiraImage[]
  readonly onChange: (images: readonly MiraImage[]) => void
  readonly tools: MiraImageTools
  /** True while an ask is in flight: a photograph must not change under a request. */
  readonly disabled: boolean
}

const EDGE_LABELS: Readonly<Record<CropEdge, MessageKey>> = {
  left: 'mira.image.edgeLeft',
  top: 'mira.image.edgeTop',
  right: 'mira.image.edgeRight',
  bottom: 'mira.image.edgeBottom',
}

const percent = (fraction: number): number => Math.round(fraction * 100)

interface Chosen {
  readonly file: File
  readonly url: string
}

export function MiraImages({ images, onChange, tools, disabled }: MiraImagesProps): JSX.Element {
  const { t } = useTranslation()
  const [chosen, setChosen] = useState<Chosen | null>(null)
  const [box, setBox] = useState<CropBox>(WHOLE_IMAGE)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const dragFrom = useRef<{ readonly x: number; readonly y: number } | null>(null)
  // The drag is measured against the photograph's own rectangle and not the surface's. They are the
  // same box by the CSS that shrink-wraps the surface, and they were not before it: the box's
  // percentages are of the surface while `cropPixels` multiplies by the image's own pixels, so an
  // image narrower than its container sent a band from the middle of the slip when the shopkeeper
  // had boxed the right-hand half of it.
  const imageRef = useRef<HTMLImageElement | null>(null)
  // What is in an edge field while it is being typed. A controlled number input whose value is
  // rejected snaps back, so with Top at 40 there was no way to type 60 into Bottom: the first
  // keystroke is 6, `moveCropEdge` refuses it for over-shrinking, React re-renders the field at
  // 100, and the next keystroke appends to that. Forty presses of ArrowUp was the only route left.
  const [typing, setTyping] = useState<{ readonly edge: CropEdge; readonly text: string } | null>(
    null,
  )

  // The object URL is a handle on a file the browser is holding open, and it has to be released or
  // a till left on a photograph it never sent holds it until the tab closes, which a cheap tablet
  // cannot spare.
  //
  // **Keyed on `chosen`, which the first version got wrong.** It was a cleanup with no dependency
  // array, so it ran after every re-render and revoked the preview of the photograph still on
  // screen: any re-render of the panel, including a keystroke in the crop fields, took the URL
  // away. Chrome keeps painting an image it has already decoded, so it would not have looked
  // broken, and the fake `release` in the suite is a no-op, so nothing saw it. Now the cleanup
  // belongs to one file: it fires when that file is replaced, discarded, or the panel unmounts.
  useEffect(() => {
    if (chosen === null) return
    const { url } = chosen
    return () => {
      tools.release(url)
    }
  }, [chosen, tools])

  const full = images.length >= MIRA_MAX_IMAGES

  const choose = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.currentTarget.files?.[0]
    // Cleared so the same file can be chosen again after a discard: a file input holds its value
    // and a second choice of the same photograph would fire no change event.
    event.currentTarget.value = ''
    if (file === undefined) return
    setChosen({ file, url: tools.previewUrl(file) })
    setBox(WHOLE_IMAGE)
    setFailed(false)
    setTyping(null)
  }

  const discard = (): void => {
    setChosen(null)
    setBox(WHOLE_IMAGE)
    setTyping(null)
  }

  const keepsTooMuch = cropKeepsTooMuch(box)
  const ready = chosen !== null && cropChosen(box) && !keepsTooMuch && !busy && !disabled

  const use = async (): Promise<void> => {
    if (!ready || chosen === null) return
    setBusy(true)
    setFailed(false)
    try {
      onChange([...images, await confirmCrop(tools.encode, chosen.file, box)])
      discard()
    } catch {
      // A crop that could not be encoded is the device's limit or a crop too large to compress, and
      // both are the person's to retry. `AI-005`: the manual path is whole, so this is a line on
      // the screen and not a failure.
      //
      // **Everything is caught, which it was not.** Only `NotCropped` was, and anything else was
      // rethrown out of `void use()` as an unhandled rejection: the button cleared its busy state
      // and looked pressable, and nothing appeared on screen, forever. An Android file picker does
      // not reliably honour the accept filter and a current phone stores camera output as HEIC,
      // which `createImageBitmap` rejects with a DOMException; an old WebView has no
      // `createImageBitmap` at all and throws a TypeError. `browser-images.ts` now turns both of
      // those into `NotCropped`, and this catch is what covers the ones nobody has thought of. The
      // cost is that a programming bug in here reads to a shopkeeper as a photograph this device
      // could not prepare, and the alternative is a button that silently does nothing.
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (disabled) return
    dragFrom.current = { x: event.clientX, y: event.clientY }
    // Without capture, a drag released outside the surface fires `pointerup` on another element and
    // this handler never runs: the gesture is lost and the start point stays set until the next
    // press. Dragging past the edge of the photograph is the ordinary way to say "everything below
    // this line". Guarded because jsdom has no pointer capture.
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const onPointerCancel = (): void => {
    dragFrom.current = null
  }

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const from = dragFrom.current
    dragFrom.current = null
    if (from === null) return
    const image = imageRef.current
    if (image === null) return
    const drawn = cropFromDrag(
      from,
      { x: event.clientX, y: event.clientY },
      image.getBoundingClientRect(),
    )
    // Null is a tap or a drag too small to be a crop, and the box it had is kept: replacing it with
    // a sliver because a finger brushed the photograph is the wrong way to be wrong.
    if (drawn !== null) setBox(drawn)
  }

  return (
    <div className="lm-mira__images">
      <h3 className="lm-mira__subheading">{t('mira.image.heading')}</h3>
      <p className="lm-hint">{t('mira.image.privacy')}</p>

      {images.length > 0 ? (
        <ul className="lm-mira__imageList">
          {images.map((image, index) => (
            <li key={image.data.slice(0, 32) + String(index)}>
              {t('mira.image.item', { position: index + 1 })}{' '}
              <button
                type="button"
                className="lm-button lm-button--secondary"
                disabled={disabled}
                onClick={() => {
                  onChange(images.filter((_, at) => at !== index))
                }}
              >
                {t('mira.image.remove')}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {chosen === null ? (
        <>
          {full ? (
            <p className="lm-hint">{t('mira.image.full')}</p>
          ) : (
            <label className="lm-field">
              <span className="lm-field__label">{t('mira.image.choose')}</span>
              <input
                className="lm-input"
                type="file"
                accept={MIRA_PICKER_ACCEPT}
                disabled={disabled}
                onChange={choose}
              />
            </label>
          )}
        </>
      ) : (
        <div className="lm-mira__crop">
          <p className="lm-hint">{t('mira.image.cropHint')}</p>
          <div
            className="lm-mira__cropSurface"
            role="img"
            aria-label={t('mira.image.surface')}
            style={{ position: 'relative', touchAction: 'none' }}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
          >
            <img src={chosen.url} alt="" className="lm-mira__cropImage" ref={imageRef} />
            <div
              className="lm-mira__cropBox"
              style={{
                position: 'absolute',
                left: `${String(percent(box.left))}%`,
                top: `${String(percent(box.top))}%`,
                right: `${String(100 - percent(box.right))}%`,
                bottom: `${String(100 - percent(box.bottom))}%`,
              }}
            />
          </div>
          {CROP_EDGES.map((edge) => (
            <label key={edge} className="lm-field lm-field--inline">
              <span className="lm-field__label">{t(EDGE_LABELS[edge])}</span>
              <input
                className="lm-input"
                type="number"
                inputMode="numeric"
                dir="ltr"
                min={0}
                max={100}
                step={1}
                value={typing?.edge === edge ? typing.text : percent(box[edge])}
                disabled={disabled || busy}
                onChange={(event) => {
                  const text = event.currentTarget.value
                  const typed = Number(text)
                  // The text is kept whether or not the box takes it, so a value typed digit by
                  // digit can pass through a state `moveCropEdge` refuses on its way to one it
                  // accepts. The box is the source of truth and the draft is dropped on blur, so
                  // what the field shows after focus leaves is always the box.
                  setTyping({ edge, text })
                  if (text === '' || !Number.isFinite(typed)) return
                  setBox((previous) => moveCropEdge(previous, edge, typed / 100))
                }}
                onBlur={() => {
                  setTyping(null)
                }}
              />
            </label>
          ))}
          <div className="lm-screen__actions">
            <button
              type="button"
              className="lm-button lm-button--primary"
              aria-disabled={!ready}
              onClick={() => {
                void use()
              }}
            >
              {t('mira.image.use')}
            </button>
            <button
              type="button"
              className="lm-button lm-button--secondary"
              disabled={busy || disabled}
              onClick={discard}
            >
              {t('mira.image.discard')}
            </button>
          </div>
        </div>
      )}

      <p className="lm-hint" role="status">
        {busy
          ? t('mira.image.preparing')
          : chosen !== null && !cropChosen(box)
            ? t('mira.image.useBlocked')
            : chosen !== null && keepsTooMuch
              ? t('mira.image.keepsTooMuch')
              : images.length > 0
                ? t('mira.image.ready', { count: images.length })
                : null}
      </p>
      {failed ? (
        <p className="lm-error" role="alert">
          {t('mira.image.failed')}
        </p>
      ) : null}
    </div>
  )
}
